import React, { useMemo } from 'react';
import moment from 'moment';
import { FieldValues, useController } from 'react-hook-form';

import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';

import TextField, { TextFieldProps } from '@mui/material/TextField';

import { FormField } from '../types';

export type FormDateTimeSelectorProps<TField extends FieldValues, TDate> = {
  DateTimePickerProps?: Omit<DateTimePickerProps<string, TDate>, 'renderInput' | 'onChange' | 'value'>;
} & Omit<TextFieldProps, 'required'> &
  FormField<TField>;

const FormDateTimeSelector = <TField extends FieldValues, TDate>(props: FormDateTimeSelectorProps<TField, TDate>) => {
  const { name, control, rules, defaultFormValue, shouldUnregister, DateTimePickerProps = {}, ...textFieldProps } = props;

  const { field, fieldState } = useController({
    name,
    control,
    defaultValue: defaultFormValue,
    shouldUnregister,
    rules: {
      required: rules?.required,
    },
  });
  const { error } = fieldState;
  const hasError = !!error;

  const momentValue = useMemo(() => {
    if (!field.value) return null;
    const parsed = moment(field.value);
    return parsed.isValid() ? parsed : null;
  }, [field.value]);

  return (
    <DateTimePicker
      {...DateTimePickerProps}
      value={momentValue}
      onChange={(newValue?: any) => {
        if (newValue == null) {
          field.onChange(null);
          return;
        }
        if (newValue.isValid?.()) {
          field.onChange(newValue.format());
        }
      }}
      onAccept={(newValue?: any) => {
        if (newValue?.isValid?.()) {
          field.onChange(newValue.format());
        }
      }}
      renderInput={(params: any) => (
        <TextField
          variant="standard"
          size="small"
          {...textFieldProps}
          {...params}
          required={!!rules?.required}
          error={!!hasError}
          helperText={error?.message || undefined}
        />
      )}
    />
  );
};

export default FormDateTimeSelector;
